import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

export type LanguageCode = "nl" | "en" | "fr";

interface Language {
  code: LanguageCode;
  name: string;
  native_name: string;
  is_default: boolean;
}

interface LanguageContextType {
  currentLanguage: LanguageCode;
  languages: Language[];
  setLanguage: (code: LanguageCode) => void;
  isLoading: boolean;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Route mappings per language
const routeMappings: Record<LanguageCode, Record<string, string>> = {
  nl: {
    "/": "/",
    "/producten": "/producten",
    "/over-ons": "/over-ons",
    "/nieuws": "/nieuws",
    "/contact": "/contact",
    "/partner-registratie": "/partner-registratie",
    "/trainingen": "/trainingen",
    "/firewalls": "/firewalls",
    "/oplossingen": "/oplossingen",
  },
  en: {
    "/": "/",
    "/producten": "/products",
    "/over-ons": "/about",
    "/nieuws": "/news",
    "/contact": "/contact",
    "/partner-registratie": "/partner-registration",
    "/trainingen": "/trainings",
    "/firewalls": "/firewalls",
    "/oplossingen": "/solutions",
  },
  fr: {
    "/": "/",
    "/producten": "/produits",
    "/over-ons": "/a-propos",
    "/nieuws": "/actualites",
    "/contact": "/contact",
    "/partner-registratie": "/inscription-partenaire",
    "/trainingen": "/formations",
    "/firewalls": "/pare-feux",
    "/oplossingen": "/solutions-fr",
  },
};

// Reverse mappings to find canonical route
const reverseRouteMappings: Record<string, string> = {};
Object.entries(routeMappings).forEach(([, routes]) => {
  Object.entries(routes).forEach(([canonical, localized]) => {
    reverseRouteMappings[localized] = canonical;
  });
});

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [currentLanguage, setCurrentLanguage] = useState<LanguageCode>("nl");
  const [languages, setLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchLanguages();
    // Check for saved language preference
    const savedLang = localStorage.getItem("language") as LanguageCode;
    if (savedLang && ["nl", "en", "fr"].includes(savedLang)) {
      setCurrentLanguage(savedLang);
    }
  }, []);

  const fetchLanguages = async () => {
    try {
      const { data, error } = await supabase
        .from("languages")
        .select("*")
        .eq("is_active", true)
        .order("code");

      if (error) throw error;
      setLanguages(data as Language[]);
    } catch (error) {
      console.error("Error fetching languages:", error);
      // Fallback to default languages
      setLanguages([
        { code: "nl", name: "Dutch", native_name: "Nederlands", is_default: true },
        { code: "en", name: "English", native_name: "English", is_default: false },
        { code: "fr", name: "French", native_name: "Français", is_default: false },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const setLanguage = (code: LanguageCode) => {
    setCurrentLanguage(code);
    localStorage.setItem("language", code);
    
    // Navigate to the localized route
    const currentPath = location.pathname;
    const canonicalPath = reverseRouteMappings[currentPath] || currentPath;
    const newPath = routeMappings[code]?.[canonicalPath] || currentPath;
    
    if (newPath !== currentPath) {
      navigate(newPath);
    }
  };

  // Simple translation function (placeholder for now)
  const t = (key: string) => key;

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        languages,
        setLanguage,
        isLoading,
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};
